import { PrimeReactPTOptions } from 'primereact/api'
import { OktaAuthOptions } from '@okta/okta-auth-js'

import { accordionConfig } from './primereact/accordion'
import { calendarConfig } from './primereact/calendar'
import { carouselConfig } from './primereact/carousel'
import { dataTableConfig } from './primereact/dataTable'
import { dialogConfig } from './primereact/dialog'
import { dropdownConfig } from './primereact/dropdown'
import { floatlabelConfig } from './primereact/floatlabel'
import { galleriaConfig } from './primereact/galleria'
import { inputSwitchConfig } from './primereact/inputSwitch'
import { inputTextConfig } from './primereact/inputText'
import { inputTextareaConfig } from './primereact/inputTextarea'
import { menubarConfig } from './primereact/menubar'
import { multiSelectConfig } from './primereact/multiSelect'
import { passwordConfig } from './primereact/password'
import { ratingConfig } from './primereact/rating'
import { sidebarConfig } from './primereact/sidebar'
import { toolbarConfig } from './primereact/toolbar'
import { toastConfig } from './primereact/toast'
import { tooltipConfig } from './primereact/tooltip'
import { inputIconConfig } from './primereact/inputicon'
import { fetchDecodedTokens, MkTokensDTO } from '../services/tokensService'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets a required environment variable, throwing an error if not found
 * @param key - The environment variable key
 * @returns The trimmed environment variable value
 * @throws Error if the environment variable is missing or empty
 */
const req = (key: string): string => {
  const raw = process.env[key]
  const val = typeof raw === 'string' ? raw.trim() : raw
  if (!val) {
    throw new Error(`Missing required environment variable: ${key}`)
  }
  return val
}

/**
 * Gets an optional environment variable, returning undefined if not found
 * @param key - The environment variable key
 * @returns The trimmed environment variable value or undefined
 */
const opt = (key: string): string | undefined => {
  const raw = process.env[key]
  const val = typeof raw === 'string' ? raw.trim() : raw
  return val || undefined
}

// ============================================================================
// Token Cache Management
// ============================================================================

// Token cache for API-fetched tokens
let tokenCache: Partial<MkTokensDTO> | null = null
let tokenCachePromise: Promise<Partial<MkTokensDTO> | null> | null = null

/**
 * Gets a token value from the API-fetched token cache only
 * @param tokenKey - The token key in the MkTokensDTO interface
 * @returns The token value from cache or empty string
 */
const getTokenValue = (tokenKey: keyof MkTokensDTO): string => {
  // Return cached token if available
  if (tokenCache && tokenCache[tokenKey]) {
    return tokenCache[tokenKey] || ''
  }

  // No fallback - rely only on /tokens API call
  return ''
}

/**
 * Initializes token cache by fetching from API
 * Prevents multiple concurrent requests by reusing the same promise
 */
const initializeTokenCache = async (): Promise<void> => {
  if (tokenCachePromise) {
    tokenCache = await tokenCachePromise
    return
  }

  tokenCachePromise = fetchDecodedTokens()
  tokenCache = await tokenCachePromise
}

// Initialize token cache (non-blocking)
const isTestEnv = typeof process !== 'undefined' && typeof process.env !== 'undefined' && (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined)

if (typeof window !== 'undefined' && !isTestEnv) {
  initializeTokenCache().catch((error) => {
    console.warn('Failed to initialize token cache:', error)
  })
}

// ============================================================================
// Environment Variables
// ============================================================================

// Core Configuration
const LAUNCHDARKLY_CLIENT_ID = req('LAUNCHDARKLY_CLIENT_ID')

// Brand Configuration
const MK_DEFAULT_AUTH_BRAND = opt('MK_DEFAULT_AUTH_BRAND')
const MK_DEFAULT_THEME_BRAND = opt('MK_DEFAULT_THEME_BRAND')
const MK_SWITCHER_BRANDS = req('MK_SWITCHER_BRANDS')

// AEM Configuration
const AEM_BASE_URL = opt('AEM_BASE_URL')
// TODO: consolidate AEM URLS once we have a single source of truth
const AEM_XF_BASE_URL = opt('AEM_XF_BASE_URL')

// Okta Configuration
const OKTA_CLIENT_ID = req('OKTA_CLIENT_ID')
const OKTA_CLIENT_ISSUER = req('OKTA_CLIENT_ISSUER')
const OKTA_CLIENT_REDIRECT_URI = req('OKTA_CLIENT_REDIRECT_URI')

// ============================================================================
// Brand Resolution Logic
// ============================================================================

// Fallback logic: if only one provided, use it for both; if neither, throw with a clear message
const RESOLVED_DEFAULT_AUTH_BRAND = MK_DEFAULT_AUTH_BRAND || MK_DEFAULT_THEME_BRAND
const RESOLVED_DEFAULT_THEME_BRAND = MK_DEFAULT_THEME_BRAND || MK_DEFAULT_AUTH_BRAND
if (!RESOLVED_DEFAULT_AUTH_BRAND || !RESOLVED_DEFAULT_THEME_BRAND) {
  throw new Error('Missing required environment variable(s): MK_DEFAULT_AUTH_BRAND and/or MK_DEFAULT_THEME_BRAND')
}

// ============================================================================
// Configuration Factory Functions
// ============================================================================

/**
 * Creates the OIDC configuration for Okta authentication
 * Handles dynamic redirect URI resolution for cross-domain scenarios
 * @returns OktaAuthOptions with optional apiToken
 */
const createOidcConfig = () => {
  const oidcConfigs = {
    scopes: [
      'openid',
      'profile',
      'email',
      'offline_access',
      // 'okta.myAccount.profile.read',
      // 'okta.myAccount.profile.manage',
      // 'okta.myAccount.email.manage',
      // 'okta.myAccount.phone.manage',
      // 'okta.myAccount.password.manage',
      // 'okta.myAccount.mfa.manage',
    ],
    responseType: ['code'],
    pkce: true,
    // Okta best-practice defaults for SPA multi-tab behavior:
    // keep tokens in localStorage and sync updates (login/logout/renew) across tabs.
    tokenManager: {
      storage: 'localStorage',
      autoRenew: true,
      autoRemove: true,
      syncStorage: true,
    },
  }

  // Use current window location for redirect URI to support cross-domain scenarios
  // Fall back to environment variable if window is not available (SSR)
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : process.env.MKFRONT_HOST || ''

  const rawRedirect = OKTA_CLIENT_REDIRECT_URI || ''
  const isAbsolute = /^https?:\/\//i.test(rawRedirect)
  let pathPart: string
  if (isAbsolute) {
    pathPart = rawRedirect
  } else {
    pathPart = rawRedirect.startsWith('/') ? rawRedirect : `/${rawRedirect}`
  }
  const redirectUri = isAbsolute ? pathPart : `${baseUrl}${pathPart}`

  return {
    ...oidcConfigs,
    clientId: OKTA_CLIENT_ID,
    issuer: OKTA_CLIENT_ISSUER,
    redirectUri,
  } as OktaAuthOptions & { apiToken?: string }
}

// ============================================================================
// Main Configuration Object
// ============================================================================

/**
 * Main application configuration object
 * Contains all configuration settings for the application including:
 * - MK-specific settings (brands, storage, API keys)
 * - GCP settings (Google Maps API)
 * - OIDC settings (Okta authentication)
 * - PrimeReact component configurations
 * - Tailwind CSS settings
 */
// Create a getter function for GCP key that always reads from current tokenCache
const getGcpKey = (): string => getTokenValue('googleMapsApiKey')

export const configs: {
  mk: {
    storageSecret?: string
    launchDarklyClientId: string
    brands: string[]
    defaultAuthBrand: string
    defaultThemeBrand: string
    aemSiteUrl?: string
    aemXfBaseUrl?: string
    dtApiToken?: string
  }
  gcp: {
    key: string
  }
  oidc: OktaAuthOptions & { apiToken?: string }
  primereact: PrimeReactPTOptions
  tailwind: object
} = {
  mk: {
    storageSecret: getTokenValue('mkStorageSecret'),
    launchDarklyClientId: LAUNCHDARKLY_CLIENT_ID,
    brands: MK_SWITCHER_BRANDS.split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    defaultAuthBrand: RESOLVED_DEFAULT_AUTH_BRAND,
    defaultThemeBrand: RESOLVED_DEFAULT_THEME_BRAND,
    aemSiteUrl: AEM_BASE_URL,
    aemXfBaseUrl: AEM_XF_BASE_URL,
    dtApiToken: getTokenValue('dtApiToken'),
  },
  gcp: {
    get key() {
      return getGcpKey()
    },
  },
  oidc: createOidcConfig(),
  primereact: {
    accordion: accordionConfig,
    calendar: calendarConfig,
    carousel: carouselConfig,
    datatable: dataTableConfig,
    dialog: dialogConfig,
    dropdown: dropdownConfig,
    floatlabel: floatlabelConfig,
    galleria: galleriaConfig,
    inputSwitch: inputSwitchConfig,
    iconfield: {
      root: {
        className: 'relative',
      },
    },
    inputicon: inputIconConfig,
    inputText: inputTextConfig,
    inputTextarea: inputTextareaConfig,
    menubar: menubarConfig,
    multiselect: multiSelectConfig,
    password: passwordConfig,
    rating: ratingConfig,
    sidebar: sidebarConfig,
    toolbar: toolbarConfig,
    toast: toastConfig,
    tooltip: tooltipConfig,
  } as PrimeReactPTOptions,
  tailwind: {},
}

// ============================================================================
// Type Exports
// ============================================================================

export type Configs = typeof configs
export type ConfigKey = keyof Configs //  .. TODO: remove if unused
export type PrimeConfig = Configs['primereact'] //  .. TODO: remove if unused
